import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import {
  DoubleSide,
  Group,
  Mesh,
  MeshPhysicalNodeMaterial,
  MeshStandardNodeMaterial,
  Object3D,
  type Material,
} from 'three/webgpu';

import { COLORS } from '../core/constants';
import { useAppStore } from '../store/appStore';
import { MODELS } from './assets';
import { createBurn } from './burn';

const REVEAL_SECONDS = 1.6;
/** How long the title card takes to burn off once clicked. */
export const INTRO_FADE_SECONDS = 1.2;
const PARALLAX_EASE = 5;
/** How fast the burn's centre closes on the click, per second. */
const ORIGIN_EASE = 18;
const BLOCK_COLORS = [COLORS.yellow, COLORS.red, COLORS.green, COLORS.blue];

/**
 * The title card: the logo, a plate behind it, and a slow-turning field of
 * blocks. It burns on out of the middle of the screen, and clicking anywhere
 * burns it back off into the point that was clicked and hands over to the
 * instrument.
 *
 * Its key light lives in `Lights` rather than here: the renderer compiles every
 * material against the set of lights in the scene, so a light that left with
 * the card would have the whole instrument recompiling on its first frame.
 */
export function Intro() {
  const { scene: model } = useGLTF(MODELS.logo);
  const group = useRef<Group>(null);
  const phase = useAppStore((state) => state.phase);
  const finishIntro = useAppStore((state) => state.finishIntro);

  // Radial, so the card opens out of the middle of the screen and collapses
  // back into a point — the middle again if it is never told otherwise, and the
  // click once there is one. The cells are finest at that point and open up
  // toward the edges, which reads as the burn having somewhere it came from.
  // One burn drives both directions.
  const burn = useMemo(
    () => createBurn({ radial: true, density: 34, growth: 2.6, colour: COLORS.yellow, seed: 3 }),
    [],
  );

  const parts = useMemo(() => {
    const logoMaterial = new MeshPhysicalNodeMaterial({
      color: COLORS.green,
      roughness: 0.35,
      metalness: 0.1,
      transparent: true,
    });
    const textMaterial = new MeshStandardNodeMaterial({
      color: COLORS.pure_white,
      roughness: 0.6,
      transparent: true,
    });
    const plateMaterial = new MeshStandardNodeMaterial({
      color: COLORS.black,
      roughness: 0.9,
      transparent: true,
    });
    const accentMaterial = new MeshStandardNodeMaterial({
      color: COLORS.red,
      roughness: 0.9,
      transparent: true,
    });

    let logo: Object3D = new Object3D();
    let blocks: Object3D = new Object3D();
    const materials: Material[] = [logoMaterial, textMaterial, plateMaterial, accentMaterial];

    model.traverse((child) => {
      if (child.name === 'Logo') {
        logo = child;
        logo.scale.set(1, 5, 1);
      }
      if (child.name === 'Lines') blocks = child;
      if (!(child instanceof Mesh)) return;

      if (child.name === 'Experience') child.material = textMaterial;
      else if (child.name === 'Backface') child.material = plateMaterial;
      else if (child.name === 'Backface2') child.material = accentMaterial;
      else if (/Block/.test(child.name)) {
        const material = new MeshStandardNodeMaterial({
          color: BLOCK_COLORS[Math.floor(Math.random() * BLOCK_COLORS.length)],
          roughness: 1,
          transparent: true,
          side: DoubleSide,
        });
        materials.push(material);
        child.material = material;
      } else child.material = logoMaterial;
    });

    return { logo, blocks, materials };
  }, [model]);

  // After the commit rather than during it, so the pair survives React mounting
  // this twice: the cleanup lets the materials go and the remount takes them
  // again, where applying during render would leave the second mount bare.
  useLayoutEffect(() => {
    burn.applyTo(model);
    return () => burn.dispose();
  }, [burn, model, parts]);

  useEffect(() => {
    return () => {
      for (const material of parts.materials) material.dispose();
    };
  }, [parts]);

  useFrame((state, delta) => {
    if (!group.current) return;

    if (phase === 'intro') {
      burn.progress.value = Math.min(1, burn.progress.value + delta / REVEAL_SECONDS);

      const ease = 1 - Math.exp(-PARALLAX_EASE * delta);
      const { x, y } = state.pointer;
      parts.logo.rotation.set(1.5 + y * 0.02, 0, x * 0.1);
      group.current.rotation.x += (y * 0.05 - group.current.rotation.x) * ease;
      group.current.rotation.y += (x * 0.01 - group.current.rotation.y) * ease;
      parts.blocks.rotateY(delta * 0.06);
      parts.blocks.traverse((child) => {
        if (child.name !== 'Lines') child.rotateZ(delta * 0.06);
      });
      return;
    }

    if (phase !== 'dissolving') return;

    // Moving the origin re-lays the lattice, which passes unseen while the card
    // is whole and would jump if it were part way in — so the common case takes
    // the click outright and a click that beats the reveal glides instead.
    const target = useAppStore.getState().burnOrigin;
    if (burn.progress.value >= 1) {
      burn.origin.value.set(target.x, target.y);
    } else {
      const originEase = 1 - Math.exp(-ORIGIN_EASE * delta);
      burn.origin.value.x += (target.x - burn.origin.value.x) * originEase;
      burn.origin.value.y += (target.y - burn.origin.value.y) * originEase;
    }

    // Everything drops and burns away over the same window, then hands off.
    const step = delta / INTRO_FADE_SECONDS;
    parts.blocks.translateY(-delta * 6);

    burn.progress.value = Math.max(0, burn.progress.value - step);
    if (burn.progress.value === 0) finishIntro();
  });

  if (phase === 'playing') return null;

  return (
    <group ref={group} scale={0.3} position={[0, 0, 2]}>
      <primitive object={model} />
    </group>
  );
}
